import { Children } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { List, type RowComponentProps } from 'react-window';
import { components } from 'react-select';
import type { GroupBase, MenuListProps } from 'react-select';

const ITEM_HEIGHT = 36;
/** Below this, default MenuList is cheaper than virtual list overhead */
const VIRTUAL_THRESHOLD = 24;

type MenuRowData = { rows: ReactNode[] };

function MenuRow(props: RowComponentProps<MenuRowData>) {
    const { index, style, ariaAttributes, rows } = props;
    return (
        <div style={style as CSSProperties} {...ariaAttributes}>
            {rows[index]}
        </div>
    );
}

/**
 * Renders only visible react-select options — fixes lag with hundreds of items.
 * @see https://react-select.com/components#replacing-components
 */
export function VirtualizedMenuList<Option, IsMulti extends boolean>(
    props: MenuListProps<Option, IsMulti, GroupBase<Option>>,
) {
    const rows = Children.toArray(props.children);
    const count = rows.length;

    if (count <= VIRTUAL_THRESHOLD) {
        return <components.MenuList {...props} />;
    }

    const maxHeight = props.maxHeight;
    const listHeight = Math.min(maxHeight, Math.max(count * ITEM_HEIGHT, ITEM_HEIGHT));

    return (
        <components.MenuList {...props}>
            <List<MenuRowData>
                rowHeight={ITEM_HEIGHT}
                rowCount={count}
                rowProps={{ rows }}
                style={{ height: listHeight, width: '100%' }}
                rowComponent={MenuRow}
            />
        </components.MenuList>
    );
}
